import { capitalCase } from 'change-case';

import type { ReportComponent, ResourceComment } from '@/types';
import { dayjs } from '@/utilities';

export function commenterName(comment: ResourceComment) {
    const user = comment.user ?? comment.member?.user;
    const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

    return fullName || user?.emailAddress || 'Unknown';
}

const stringValue = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

const reportComponentIdFromComponentKey = (component?: string) => {
    const prefix = 'reportComponent:';

    return component?.startsWith(prefix) ? component.slice(prefix.length) : undefined;
};

export function commentTarget(comment: ResourceComment, reportComponentsById: Record<string, ReportComponent> = {}) {
    const metadata = comment.metadata ?? {};
    const metadataTarget = (comment.metadata?.target ?? {}) as Record<string, unknown>;
    const metadataTargetAttrs = (metadataTarget.attrs ?? {}) as Record<string, unknown>;
    const type = stringValue(metadataTarget.type);
    const quote = stringValue(metadataTarget.quote);
    const nodeType = stringValue(metadataTarget.nodeType);
    const componentId =
        stringValue(metadataTarget.componentId) ??
        stringValue(metadataTarget.reportComponentId) ??
        stringValue(metadataTargetAttrs.reportComponentId) ??
        reportComponentIdFromComponentKey(comment.component);
    const reportComponent = componentId ? reportComponentsById[componentId] : undefined;
    const reportComponentLabel =
        stringValue(metadataTarget.label) ??
        stringValue(metadataTarget.reportComponentName) ??
        stringValue(metadataTarget.componentName) ??
        stringValue(metadata.reportComponentName) ??
        stringValue(metadata.componentName) ??
        stringValue(metadataTargetAttrs.name) ??
        stringValue(metadataTargetAttrs.title) ??
        reportComponent?.name;
    const isReportComponentTarget = Boolean(
        componentId ||
        nodeType === 'reportComponent' ||
        nodeType === 'reportComponentEmbed' ||
        type === 'Report Component',
    );
    const targetLabel = reportComponentLabel ?? (isReportComponentTarget ? 'Report Component' : undefined);
    const headingLevel =
        nodeType === 'heading' && typeof metadataTargetAttrs.level === 'number' ? metadataTargetAttrs.level : undefined;
    const label =
        headingLevel !== undefined
            ? `Heading ${headingLevel}`
            : nodeType && targetLabel?.toLocaleLowerCase() === nodeType.toLocaleLowerCase()
              ? capitalCase(targetLabel)
              : targetLabel;
    const from = typeof metadataTarget.from === 'number' ? metadataTarget.from : undefined;
    const position = typeof metadataTarget.position === 'number' ? metadataTarget.position : undefined;
    const to = typeof metadataTarget.to === 'number' ? metadataTarget.to : undefined;
    const breadcrumbs = Array.isArray(metadataTarget.breadcrumbs)
        ? metadataTarget.breadcrumbs.map(stringValue).filter((breadcrumb): breadcrumb is string => Boolean(breadcrumb))
        : [];

    if (!label && !type && !quote && !nodeType && !comment.component) {
        return undefined;
    }

    return {
        breadcrumbs,
        componentId,
        from,
        label:
            label ??
            (nodeType ? capitalCase(nodeType) : undefined) ??
            (comment.component === 'memo' ? 'Memo' : comment.component) ??
            'Memo',
        nodeType,
        position,
        quote,
        raw: metadataTarget,
        to,
        type: type ?? nodeType ?? (isReportComponentTarget ? 'component' : 'memo'),
    };
}

export function childCommentsFor(comments: ResourceComment[], parentId: string) {
    return comments
        .filter((comment) => comment.parentId === parentId)
        .sort((first, second) => dayjs(first.createdAt).valueOf() - dayjs(second.createdAt).valueOf());
}

export function upsertComment(comments: ResourceComment[], comment: ResourceComment) {
    const exists = comments.some((item) => item.id === comment.id);

    return exists ? comments.map((item) => (item.id === comment.id ? comment : item)) : [comment, ...comments];
}

const investmentAnalysisRouteByComponent: Record<string, string> = {
    balanceSheet: 'balance',
    cashFlows: 'cashflows',
    dividends: 'dividends',
    filings: 'filings',
    income: 'income',
    multiples: 'multiples',
    returns: 'returns',
    structure: 'structure',
};

export function investmentAnalysisHref(resourceId: string, component?: string) {
    const componentKey = component?.replace(/^(chart|table)-/, '');
    const route = componentKey ? investmentAnalysisRouteByComponent[componentKey] : undefined;

    return route ? `/investments/${resourceId}/analysis/${route}` : undefined;
}

const workspaceResourcePathByType: Partial<Record<ResourceComment['resourceType'], string>> = {
    dashboards: '/dashboards',
    folders: '/folders',
    investments: '/investments',
    lists: '/lists',
    memos: '/memos',
    pipelines: '/pipelines',
    teams: '/settings/workspace/permissions/teams',
};

export function workspaceResourceHref(resourceType: ResourceComment['resourceType'], resourceId: string) {
    if (resourceType === 'tasks') {
        return `/relations/tasks?task=${encodeURIComponent(resourceId)}`;
    }

    const resourcePath = workspaceResourcePathByType[resourceType];

    return resourcePath ? `${resourcePath}/${resourceId}` : undefined;
}
